import { ApolloError, isApolloError } from '@apollo/client';
import { GraphQLError as GQLError } from 'graphql';
import { IntlShape } from 'react-intl';

import { NonNullableObject } from '@rbi-ctg/frontend';
import { messageIdMappingForDialogs } from 'pages/cart/payment/get-error-dialog-options';
import { CardTypes } from 'state/payment/types';
import { TLocalizationKey } from 'types/i18n';
import { filterMap } from 'utils/array';
import { GraphQLError } from 'utils/network';

import { GraphQLErrorCodes, IGraphqlErrorMap, paymentErrorModalMaps } from './types';

const mapErrorsWithCodes = filterMap<GQLError, NonNullableObject<GQLError>, IGQLParsedErrorCode>(
  err => !!err.extensions?.code,
  err => ({ errorCode: err.extensions.code, message: err.message }) as IGQLParsedErrorCode
);

/**
 * Given a GraphQL error, parse all the GraphQL error codes.
 */
export function parseGraphQLErrorCodes(error: GraphQLError | ApolloError): IGQLParsedErrorCode[] {
  if (isApolloError(error)) {
    return mapErrorsWithCodes(error.graphQLErrors);
  }
  return mapErrorsWithCodes(error.originalError || []);
}

/**
 * Given a GraphQL error, return the first GraphQL error code.
 */
export function getGraphQLErrorCode(error: GraphQLError | ApolloError): GraphQLErrorCodes {
  return parseGraphQLErrorCodes(error)[0]?.errorCode;
}

/**
 * Given an array of graphql errors, do any errors have an extension with the given code
 *
 * @param graphQLErrors {GraphQLError[]} An array of Apollo GraphQL errors
 * @param code {GraphQLErrorCodes} The code
 */
export const graphQLErrorsIncludeExtensionCode = (
  graphQLErrors: ApolloError['graphQLErrors'] = [],
  code: GraphQLErrorCodes
) => graphQLErrors.some(graphQLError => graphQLError?.extensions?.code === code);

export interface IErrorDialog {
  title?: string;
  body?: string;
  link?: {
    key: TLocalizationKey;
    to: string;
    text: string;
  };
  message: string;
}

export interface IGQLParsedErrorCode {
  errorCode: GraphQLErrorCodes;
  message?: string;
}

/**
 * Given an error code from the CTG GQL api, return localized error dialog content.
 *
 * Returns `undefined` if we have no dialog defined for the given error code.
 * @param parsedError RBI CTG GraphQL error code
 * @param staticStrings Defined static strings
 */
export function getErrorDialogForGraphQLCode(
  parsedError: IGQLParsedErrorCode,
  formatMessage: IntlShape['formatMessage']
): IErrorDialog | undefined {
  const { errorCode, message } = parsedError;

  const paymentModalKey = paymentErrorModalMaps[errorCode];
  const errorDialog = messageIdMappingForDialogs[paymentModalKey];
  if (!errorDialog) {
    return;
  }
  const { title: titleId, body, message: messageId } = errorDialog;
  if (errorCode === GraphQLErrorCodes.GRAPHQL_VALIDATION_FAILED && message) {
    return {
      title: formatMessage({ id: titleId }),
      body: message,
      message,
    };
  }

  return {
    title: formatMessage({ id: titleId }),
    body: formatMessage({ id: body }),
    message: formatMessage({ id: messageId }),
  };
}

/**
 * Digital Wallet payment error modal key
 * Returns the key for the digital wallet payment error modal
 * @param fdAccountId
 * @returns
 */
export const getAppleOrGooglePayError = (fdAccountId: string): TLocalizationKey | undefined => {
  const messageMapping = {
    [CardTypes.APPLE_PAY]: 'orderFailureMessageForApplePay',
    [CardTypes.GOOGLE_PAY]: 'orderFailureMessageForGooglePay',
  };

  return messageMapping[fdAccountId];
};

/**
 * Creates a dictionary from an array of gql errors using code as the lookup index
 * If a code is not supplied the key will be `GQL_ERROR_$index`
 *
 * @param {GQLError[]} gqlErrors
 *
 * @returns {IGraphqlErrorMap}
 */
export const createGqlErrorMap = (gqlErrors: GQLError[]): IGraphqlErrorMap =>
  gqlErrors.reduce((acc, error, idx) => {
    const key = `${error.extensions?.code || 'GQL_ERROR'}_${idx}`;
    acc[key] = error;
    return acc;
  }, {});

export * from './types';
